import { getBaseEmitArgs, } from '../types';
/**
 * EventEmitter mixin.
 *
 * @remarks
 * Strongly types the components `$emit` method, allowing only the mapped events to be emitted.
 *
 * @example
 * Apply mixin to the base {@link @microsoft/fast-element#FASTElement | FASTElement}
 *
 * # Import
 * ```ts
 * import { EventEmitter, SomeSystemLevelEventDetailMap } from '@genesislcap/foundation-events';
 * import { StoreEventDetailMap, TradeEntryEventDetailMap } from '../store'; // < custom app level event maps
 * ```
 *
 * # Create event map
 * ```ts
 * type EventMap = SomeSystemLevelEventDetailMap & StoreEventDetailMap & TradeEntryEventDetailMap;
 * ```
 *
 * # Apply Mixin
 * ```ts
 * export class MyComponent extends EventEmitter<EventMap>(FASTElement) {
 *   onSomeComponentEvent() {
 *     this.$emit('some-allowed-event', { foo: 'expected data structure' });
 *   }
 * }
 * ```
 *
 * @example
 * Apply mixin to a class in the inheritance chain requires a second generic type param.
 *
 * # Apply Mixin to MyComponent that extends MyBaseComponent (which might be abstract). Mixin can be applied at different levels.
 * # Please note there's a current limitation on retaining protected member access with this approach, so apply to base where possible.
 * # Also, super classes cannot emit typed events via inheritance. Each level must have an explicit map. Devs can use a Union and Pick to create these.
 * ```ts
 * export class MyComponent extends EventEmitter<EventMap, MyBaseComponent>(MyBaseComponent) {}
 * ```
 *
 * @privateRemarks
 * Failed to retain protected member access when the mixin is applied while keeping the typed emitter, we can revisit if needed.
 * So in the last example, if `MyBaseComponent` had a protected member, `MyComponent` wouldn't be able to access it with the mixin
 * applied. Public members are fine. It may be a limitation of TypeScript. Suggestions welcome, but be warned it's a time sink.
 *
 * @typeParam TEventDetailMap - The {@link EventDetailMap}.
 * @typeParam TTarget - The target class. Defaults to a union of `HTMLElement & FASTElement`.
 * @param Target - The class to apply the mixin to which ultimately extends {@link @microsoft/fast-element#FASTElement | FASTElement}.
 * @public
 */
export const EventEmitter = (Target) => {
    // @ts-ignore used here instead of `extends (Target as EventEmitterTarget<FASTElement>)` to avoid modifying return type
    class Mixin extends Target {
        $emit(...args) {
            const [type, detail, options] = getBaseEmitArgs(...args);
            return super.$emit(type, detail, options);
        }
    }
    return Mixin;
};
